import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
const ARTICLES = [{
  title: 'Understanding & Managing Daily Anxiety',
  subtitle: 'Practical grounding techniques and cognitive strategies',
  image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=800&auto=format&fit=crop'
}, {
  title: 'Improving Communication in Relationships',
  subtitle: 'Active listening skills to foster deeper connection',
  image: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?q=80&w=800&auto=format&fit=crop'
}, {
  title: 'Coping with Chronic Stress & Burnout',
  subtitle: 'Identifying warning signs and setting boundaries',
  image: 'https://images.unsplash.com/photo-1545205597-3d9d02c29597?q=80&w=800&auto=format&fit=crop'
}, {
  title: 'Building Emotional Resilience',
  subtitle: "Developing psychological flexibility for life's setbacks",
  image: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?q=80&w=800&auto=format&fit=crop'
}, {
  title: 'Navigating Grief & Loss',
  subtitle: 'Finding healthy ways to process your emotions',
  image: 'https://images.unsplash.com/photo-1474418397713-7ede21d49118?q=80&w=800&auto=format&fit=crop'
}, {
  title: 'The Power of EMDR Therapy',
  subtitle: 'How reprocessing can help heal trauma',
  image: 'https://images.unsplash.com/photo-1559757175-5700dde675bc?q=80&w=800&auto=format&fit=crop'
}];
export function ResourcesSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll);
    checkScroll();
    const interval = setInterval(() => {
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (el.scrollLeft >= maxScroll - 10) {
        el.scrollTo({
          left: 0,
          behavior: 'smooth'
        });
      } else {
        el.scrollBy({
          left: 240,
          behavior: 'smooth'
        });
      }
    }, 4000);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      clearInterval(interval);
    };
  }, []);
  const scroll = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({
      left: dir === 'left' ? -240 : 240,
      behavior: 'smooth'
    });
  };
  return <section id="resources" className="py-12 lg:py-16 bg-cream" data-id="element-457">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-458">
        <div className="flex items-end justify-between mb-8 gap-4" data-id="element-459">
          <div data-id="element-460">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-charcoal mb-2" data-id="element-461">
              Mental Health Resources
            </h2>
            <p className="text-charcoal/60 text-base font-medium" data-id="element-462">
              Insights and tools to support your wellness journey.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-463">
            <button onClick={() => scroll('left')} disabled={!canScrollLeft} className="w-9 h-9 rounded-full border border-charcoal/20 flex items-center justify-center text-charcoal/50 hover:text-charcoal hover:border-charcoal/40 transition-all disabled:opacity-20" aria-label="Scroll left" data-id="element-464">
              <ChevronLeft size={18} data-id="element-465" />
            </button>
            <button onClick={() => scroll('right')} disabled={!canScrollRight} className="w-9 h-9 rounded-full border border-charcoal/20 flex items-center justify-center text-charcoal/50 hover:text-charcoal hover:border-charcoal/40 transition-all disabled:opacity-20" aria-label="Scroll right" data-id="element-466">
              <ChevronRight size={18} data-id="element-467" />
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="flex gap-5 overflow-x-auto hide-scrollbar pb-2 -mx-4 px-4 snap-x snap-mandatory" data-id="element-468">
          {ARTICLES.map((a, i) => <article key={a.title} className="flex-none w-[200px] sm:w-[220px] group snap-start flex flex-col" data-id="element-469">
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-3 shadow-soft" data-id="element-470">
                <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-471" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent" data-id="element-472" />
              </div>
              <h3 className="font-heading text-sm font-bold text-charcoal mb-1 leading-snug group-hover:text-olive transition-colors" data-id="element-473">
                {a.title}
              </h3>
              <p className="text-charcoal/50 text-xs font-medium leading-snug mb-3 flex-grow" data-id="element-474">
                {a.subtitle}
              </p>
              <Link to="/blog" className="inline-flex items-center text-olive font-bold text-xs uppercase tracking-wide hover:text-gold transition-colors mt-auto" data-id="element-475">
                Read More
                <ArrowRight size={14} className="ml-1" data-id="element-476" />
              </Link>
            </article>)}
        </div>

        <div className="text-center mt-8" data-id="element-477">
          <Link to="/blog" className="inline-flex items-center justify-center bg-olive hover:bg-olive/90 text-white px-6 py-3 rounded-full font-bold text-base transition-all shadow-soft min-h-[48px] tracking-wide" data-id="element-478">
            View All Resources
            <ArrowRight size={16} className="ml-2" data-id="element-479" />
          </Link>
        </div>
      </div>
    </section>;
}